import cn from 'classnames';
import * as React from 'react';

import useInputValidation from '../use-input-validation';
import Text from '../text';

import { colors, Radio as Props } from './radio.types';

const RefRadio = React.forwardRef<
  HTMLInputElement,
  React.PropsWithChildren<Props>
>(
  (
    {
      children,
      color,
      description,
      isUnderline,
      label,
      name,
      showCustomError = true,
      value,
      ...restProps
    },
    ref,
  ) => {
    const [onChange, onInvalid, error] = useInputValidation();

    return (
      <label
        className={cn(
          'radio',
          { 'radio--error': error, 'radio--underline': isUnderline },
          color,
        )}
      >
        <input
          name={name}
          value={value}
          ref={ref}
          type="radio"
          onChange={e => {
            // NOTE: To not break compatibility
            if (restProps.onChange) restProps.onChange(e);
            onChange(e);
          }}
          onInvalid={e => {
            // NOTE: To not break compatibility
            if (restProps.onInvalid) restProps.onInvalid(e);
            onInvalid(e);
          }}
          {...restProps}
        />
        <div className="radio__label">
          <span className="radio__fake-element"></span>
          <span>
            {label}
            {children}
          </span>
        </div>

        {/* NOTE: This is aria-hidden because reporting of validation errors is handled by the browser */}
        {error && showCustomError && (
          <div aria-hidden="true" className="radio__error">
            {error}
          </div>
        )}

        {description && (
          <Text className="radio__description" size={Text.sizes.small}>
            {description}
          </Text>
        )}
      </label>
    );
  },
);

// NOTE: If this isn't set, stack traces say "forwardRef" instead of "Radio"
RefRadio.displayName = 'Radio';

// NOTE: The component is redeclared here because TypeScript doesn't accept assigning extra properties to `RefComponent` when it's declared
const Radio: typeof RefRadio & {
  colors: typeof colors;
} = Object.assign(RefRadio, { colors });

export default Radio;
